import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import Header from '../shared/Header';
import Footer from '../shared/Footer';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.forgotPassword(email);
      setSubmitted(true);
      toast.success('Password reset link sent! Check your email.');
    } catch (error) {
      toast.error('Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFBF9] flex flex-col">
      <Header />
      
      <div className="flex-1 flex items-center justify-center py-12 px-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8" data-testid="forgot-password-container">
            <div className="text-center mb-8">
              <h1 className="font-serif text-3xl text-[#3E2723] mb-2">Forgot Password</h1>
              <p className="text-[#786E66]">
                {submitted ? "Check your email" : "Enter your email to reset your password"}
              </p>
            </div>

            {submitted ? (
              <div className="space-y-6">
                <div className="bg-[#F3EBE1] rounded-lg p-6 text-center">
                  <Mail className="h-12 w-12 text-[#3E2723] mx-auto mb-4" />
                  <p className="text-[#1A1A1A] mb-2">Reset link sent!</p>
                  <p className="text-sm text-[#786E66]">
                    If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
                  </p>
                </div>
                <Link
                  to="/login"
                  className="w-full bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full py-3 font-medium transition-all hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#786E66]" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none transition-all"
                      placeholder="your.email@example.com"
                      data-testid="forgot-password-email-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full py-3 font-medium transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="forgot-password-submit-button"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-[#3E2723] font-medium hover:underline inline-flex items-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ForgotPasswordPage;
